import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Coffee } from "lucide-react";

type CoffeeComparisonBlockProps = {
  /** Price string for display (e.g. "€29" or "$29") */
  priceLabel?: string;
  /** Optional class for the container */
  className?: string;
  /** Compact variant for tighter layouts */
  compact?: boolean;
};

/**
 * Standalone coffee budget comparison block for use on pricing page or inline.
 * Reinforces that the plan price is "less than your weekly coffee budget".
 */
export function CoffeeComparisonBlock({
  priceLabel = "€29",
  className = "",
  compact = false,
}: CoffeeComparisonBlockProps) {
  const [visibleCoffees, setVisibleCoffees] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleCoffees((prev) => (prev < 5 ? prev + 1 : 5));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br from-amber-50/80 to-orange-50/80 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200/80 dark:border-amber-800/50 shadow-lg ${compact ? "p-5" : "p-6 md:p-8"} ${className}`}
    >
      <div className="flex h-full flex-col justify-center space-y-4">
        <h3
          className={`font-bold text-zinc-900 dark:text-zinc-100 ${compact ? "text-lg" : "text-xl md:text-2xl"}`}
        >
          That's less than your weekly coffee budget
        </h3>
        <p className="text-zinc-600 dark:text-zinc-400 text-sm md:text-base">
          {priceLabel} ≈ 5 iced lattes
        </p>
        <div className="flex justify-center items-center gap-2 md:gap-3 min-h-[72px]">
          {[...Array(5)].map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: index < visibleCoffees ? 1 : 0,
                scale: index < visibleCoffees ? 1 : 0,
              }}
              transition={{
                duration: 0.4,
                type: "spring",
                stiffness: 200,
              }}
              className="flex flex-col items-center"
            >
              <Coffee
                className={compact ? "h-8 w-8 text-amber-600 dark:text-amber-500" : "h-9 w-9 md:h-10 md:w-10 text-amber-600 dark:text-amber-500"}
              />
              <span className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-medium">
                {(() => {
                  const num = parseFloat(priceLabel.replace(/[^\d.]/g, "")) || 29;
                  const symbol = priceLabel.replace(/[\d.]/g, "").trim() || "€";
                  const perCoffee = (num / 5).toFixed(2);
                  return `${symbol}${perCoffee}`;
                })()}
              </span>
            </motion.div>
          ))}
        </div>
        <div className="space-y-1 pt-2 border-t border-amber-200/60 dark:border-amber-800/40">
          <p className="text-zinc-600 dark:text-zinc-300 font-medium text-sm md:text-base">
            But instead of 5 coffees, you get:
          </p>
          <p className="text-base md:text-lg font-semibold text-blue-600 dark:text-blue-400">
            A complete system to reach $10K MRR
          </p>
        </div>
      </div>
    </div>
  );
}
